import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { DEFAULT_LOCALE, LOCALES, translate } from '../locales';

const STORAGE_KEY = 'ui_language';

const LanguageContext = createContext(null);

function loadInitial() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && LOCALES[stored]) return stored;
  } catch { /* ignore */ }
  return DEFAULT_LOCALE;
}

export function LanguageProvider({ children }) {
  const [locale, setLocaleState] = useState(loadInitial);

  const setLocale = useCallback((next) => {
    if (!LOCALES[next]) return;
    try { localStorage.setItem(STORAGE_KEY, next); } catch { /* ignore */ }
    setLocaleState(next);
    // Reflect on <html lang> so screen readers + browser spell-check pick it up.
    if (typeof document !== 'undefined') document.documentElement.lang = next;
  }, []);

  useEffect(() => {
    if (typeof document !== 'undefined') document.documentElement.lang = locale;
  }, [locale]);

  const value = useMemo(() => ({
    locale,
    setLocale,
    locales: LOCALES,
    t: (key, params) => translate(locale, key, params),
  }), [locale, setLocale]);

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useT() {
  const ctx = useContext(LanguageContext);
  if (!ctx) {
    // Defensive fallback so components rendered outside the provider still get English text.
    return {
      locale: DEFAULT_LOCALE,
      setLocale: () => {},
      locales: LOCALES,
      t: (key, params) => translate(DEFAULT_LOCALE, key, params),
    };
  }
  return ctx;
}
